import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../services/useAuth";
import { translate } from "../../../hooks/translate";

import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import Content from "../Content";

import styles from "./Login.module.scss";
import { useTitle } from "../../../hooks/useTitle";
import { Toast, ToastContainer } from "../../../components/CustomUI/Toast";
import {
  FiUser,
  FiLock,
  FiLogIn,
  FiShield,
  FiEye,
  FiCpu,
  FiWifi,
  FiCheckCircle,
  FiLoader,
} from "react-icons/fi";

import initialData from "../../../services/database/data";

export default function Login() {
  useTitle("BigPass Panel - Giriş");

  const navigate = useNavigate();
  const { login } = useAuth();

  const [user, setUser] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [scanningActive, setScanningActive] = useState(false);
  const [securityStatus, setSecurityStatus] = useState("idle");
  const [loadingStep, setLoadingStep] = useState(0);
  const [typedText, setTypedText] = useState("");
  const [showCursor, setShowCursor] = useState(true);
  const [typingComplete, setTypingComplete] = useState(false);

  const fullText = "Geçiş Kontrol Sistemi";
  const loadingSteps = [
    "Güvenli bağlantı başlatılıyor...",
    "Kimlik bilgileri doğrulanıyor...",
    "Erişim izinleri kontrol ediliyor...",
    "Kullanıcı doğrulanıyor...",
    "Giriş işlemi tamamlanıyor...",
  ];

  useEffect(() => {
    setFadeIn(true);

    // Start security scanning animation
    const scanTimer = setTimeout(() => {
      setScanningActive(true);
    }, 1000);

    // Cycle through security status
    const statusCycle = setInterval(() => {
      setSecurityStatus((prev) => {
        const statuses = ["scanning", "analyzing", "verified", "secure"];
        const currentIndex = statuses.indexOf(prev);
        return statuses[(currentIndex + 1) % statuses.length];
      });
    }, 3000);

    return () => {
      clearTimeout(scanTimer);
      clearInterval(statusCycle);
    };
  }, []);

  // Typing animation effect
  useEffect(() => {
    const startDelay = 1500; // Delay before typing starts
    const typingSpeed = 120; // Speed between characters
    const pauseAfterTyping = 1000; // Pause after typing is complete

    const startTyping = setTimeout(() => {
      let currentIndex = 0;

      const typeNextCharacter = () => {
        if (currentIndex < fullText.length) {
          setTypedText(fullText.slice(0, currentIndex + 1));
          currentIndex++;
          setTimeout(typeNextCharacter, typingSpeed);
        } else {
          // Typing complete
          setTimeout(() => {
            setTypingComplete(true);
            setShowCursor(false);
          }, pauseAfterTyping);
        }
      };

      typeNextCharacter();
    }, startDelay);

    // Cursor blinking effect
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 500);

    return () => {
      clearTimeout(startTyping);
      clearInterval(cursorInterval);
    };
  }, []);

  // Loading step progression effect
  useEffect(() => {
    let stepInterval;
    if (loading) {
      setLoadingStep(0);
      stepInterval = setInterval(() => {
        setLoadingStep((prev) => {
          if (prev < loadingSteps.length - 1) {
            return prev + 1;
          }
          return prev;
        });
      }, 800);
    }

    return () => {
      if (stepInterval) clearInterval(stepInterval);
    };
  }, [loading]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSecurityStatus("authenticating");

      const response = await login(user.email, user.password);

      if (response.success) {
        setSecurityStatus("access-granted");
        setTimeout(() => {
          navigate("/");
        }, 1000);
      } else {
        setSecurityStatus("access-denied");
        setError(
          response.message ||
            "Giriş başarısız. Lütfen bilgilerinizi kontrol edin."
        );
      }
    } catch (error) {
      setSecurityStatus("error");
      setError(
        error.message || "Bir hata oluştu. Lütfen daha sonra tekrar deneyin."
      );
    } finally {
      setLoading(false);
      setTimeout(() => {
        setSecurityStatus("idle");
      }, 3000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!loading) {
      await handleLogin(e);
    }
  };

  const getSecurityIcon = () => {
    switch (securityStatus) {
      case "scanning":
        return <FiEye className={styles["pulse"]} />;
      case "analyzing":
        return <FiCpu className={styles["rotate"]} />;
      case "verified":
        return <FiCheckCircle className={styles["success"]} />;
      case "secure":
        return <FiShield className={styles["glow"]} />;
      case "authenticating":
        return <FiLock className={styles["pulse"]} />;
      case "access-granted":
        return <FiCheckCircle className={styles["success"]} />;
      case "access-denied":
        return <FiLock className={styles["error"]} />;
      default:
        return <FiShield className={styles["idle"]} />;
    }
  };

  const getSecurityText = () => {
    switch (securityStatus) {
      case "scanning":
        return "Ortam taranıyor...";
      case "analyzing":
        return "Güvenlik protokolleri analiz ediliyor...";
      case "verified":
        return "Kimlik doğrulandı";
      case "secure":
        return "Sistem güvenli";
      case "authenticating":
        return "Kullanıcı doğrulanıyor...";
      case "access-granted":
        return "Erişim izni verildi";
      case "access-denied":
        return "Erişim reddedildi";
      case "error":
        return "Güvenlik hatası";
      default:
        return "Kimlik doğrulama için hazır";
    }
  };

  return (
    <div
      className={`${styles["login-container"]} ${
        fadeIn ? styles["fade-in"] : ""
      }`}
    >
      {/* Loading Overlay */}
      {loading && (
        <div className={styles["loading-overlay"]}>
          <div className={styles["loading-content"]}>
            <div className={styles["loading-scanner"]}>
              <div className={styles["scanner-line"]}></div>
              <div className={styles["scanner-grid"]}>
                {[...Array(25)].map((_, i) => (
                  <div key={i} className={styles["grid-dot"]}></div>
                ))}
              </div>
            </div>

            <div className={styles["loading-icon"]}>
              <FiShield className={styles["loading-shield"]} />
              <div className={styles["loading-ring"]}></div>
            </div>

            <div className={styles["loading-text"]}>
              <h3>Erişim Doğrulanıyor</h3>
              <p>{loadingSteps[loadingStep]}</p>
            </div>

            <div className={styles["loading-progress"]}>
              <div className={styles["progress-bar"]}>
                <div
                  className={styles["progress-fill"]}
                  style={{
                    width: `${
                      ((loadingStep + 1) / loadingSteps.length) * 100
                    }%`,
                  }}
                ></div>
              </div>
              <span className={styles["progress-text"]}>
                {Math.round(((loadingStep + 1) / loadingSteps.length) * 100)}%
              </span>
            </div>

            <div className={styles["loading-dots"]}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      )}

      {/* Left side - Branding */}
      <div className={styles["login-brand"]}>
        {/* Animated Background Grid */}
        <div className={styles["grid-background"]}></div>

        {/* Security Scanning Line */}
        {scanningActive && <div className={styles["scan-line"]}></div>}

        <div className={styles["brand-content"]}>
          <div className={styles["brand-logo"]}>
            <div className={styles["security-ring"]}>{getSecurityIcon()}</div>
            <img
              src={require("../../../assets/logos/logo-2.png")}
              alt="BigPass"
              className={styles["logo"]}
            />
          </div>

          <h1 className={styles["brand-title"]}>
            <span
              className={`${styles["typing-text"]} ${
                typingComplete ? styles["typing-complete"] : ""
              }`}
            >
              {typedText}
              {!typingComplete && (
                <span
                  className={`${styles["cursor"]} ${
                    showCursor ? styles["visible"] : ""
                  }`}
                >
                  |
                </span>
              )}
            </span>
          </h1>

          <p className={styles["brand-subtitle"]}>
            Yetkili personel için güvenli kimlik doğrulama
          </p>

          {/* Real-time Security Status */}
          <div className={styles["security-status"]}>
            <div className={styles["status-indicator"]}>
              <div className={styles["status-light"]}></div>
              <span className={styles["status-text"]}>{getSecurityText()}</span>
            </div>
          </div>

          <div className={styles["security-features"]}>
            <div className={styles["feature"]}>
              <FiShield />
              <span>Çok Katmanlı Kimlik Doğrulama</span>
              <div className={styles["feature-bar"]}></div>
            </div>
            <div className={styles["feature"]}>
              <FiLock />
              <span>256-bit Şifreleme</span>
              <div className={styles["feature-bar"]}></div>
            </div>
            <div className={styles["feature"]}>
              <FiWifi />
              <span>Gerçek Zamanlı İzleme</span>
              <div className={styles["feature-bar"]}></div>
            </div>
          </div>

          {/* Circuit Pattern */}
          <div className={styles["circuit-pattern"]}></div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className={styles["login-panel"]}>
        <div className={styles["login-content"]}>
          <div className={styles["login-header"]}>
            <div className={styles["access-indicator"]}>
              <div className={styles["access-dots"]}>
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span>GÜVENLİ GİRİŞ</span>
            </div>
            <h2 className={styles["login-title"]}>
              {translate("AUTHENTICATION.ENTER_TO_CONTINUE")}
            </h2>
            <p className={styles["login-subtitle"]}>
              Sisteme erişmek için kimlik bilgilerinizi girin
            </p>
          </div>

          <form onSubmit={handleSubmit} className={styles["login-form"]}>
            <div className={styles["input-group"]}>
              <Input
                type="email"
                label="E-posta Adresi"
                placeholder={translate("AUTHENTICATION.ENTER_YOUR_EMAIL")}
                className={styles["form-input"]}
                value={user.email}
                onChange={(value) => setUser({ ...user, email: value })}
                disabled={loading}
                required
                prefix={<FiUser />}
              />
            </div>

            <div className={styles["input-group"]}>
              <Input
                type="password"
                label={translate("AUTHENTICATION.PASSWORD")}
                placeholder={translate("AUTHENTICATION.ENTER_YOUR_PASSWORD")}
                className={styles["form-input"]}
                value={user.password}
                onChange={(value) => setUser({ ...user, password: value })}
                disabled={loading}
                required
                prefix={<FiLock />}
              />
            </div>

            <Button
              type="submit"
              title={
                loading
                  ? translate("AUTHENTICATION.LOGGING_IN")
                  : translate("AUTHENTICATION.TO_ENTER")
              }
              appearance="primary-animated"
              isBlock={true}
              disabled={loading}
              iconAfter={loading ? null : <FiLogIn />}
              className={`${styles["login-button"]} ${
                loading ? styles["loading"] : ""
              }`}
            />
          </form>

          <div className={styles["login-footer"]}>
            <div className={styles["security-badges"]}>
              <span className={styles["badge"]}>SSL Güvenli</span>
              <span className={styles["badge"]}>ISO 27001</span>
              <span className={styles["badge"]}>GDPR Uyumlu</span>
            </div>
            <p>
              © {new Date().getFullYear()} BigPass.{" "}
              {translate("AUTHENTICATION.ALL_RIGHTS_RESERVED")}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <ToastContainer>
          <Toast id={Date.now()} title={error} appearance="danger" isExpanded />
        </ToastContainer>
      )}
    </div>
  );
}
